import { useEffect, useState } from 'react';

import { useReducedMotion } from './useReducedMotion.js';

import type { ReactElement } from 'react';

const RUN_MS = 1500;
const DEFAULT_DELAY_MS = 350;
/** The exponent of the ease-out: large enough that the last third of the run barely moves. */
const EASE_RATE = 10;

type CountUpProps = {
  readonly to: number;
  readonly format: (n: number) => string;
  readonly delayMs?: number;
};

const easeOut = (progress: number): number => 1 - 2 ** (-EASE_RATE * progress);

/** Counts from zero to `to`, or renders the answer outright when the viewer wants less movement. */
export const CountUp = ({ to, format, delayMs = DEFAULT_DELAY_MS }: CountUpProps): ReactElement => {
  const isMotionReduced = useReducedMotion();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (isMotionReduced) {
      return;
    }

    const startAt = performance.now() + delayMs;
    const draftFrame = { id: 0 };

    const step = (now: number): void => {
      const progress = Math.min(Math.max((now - startAt) / RUN_MS, 0), 1);
      setValue(progress >= 1 ? to : to * easeOut(progress));

      if (progress < 1) {
        draftFrame.id = requestAnimationFrame(step);
      }
    };

    draftFrame.id = requestAnimationFrame(step);

    return (): void => {
      cancelAnimationFrame(draftFrame.id);
    };
  }, [to, delayMs, isMotionReduced]);

  return <span>{format(isMotionReduced ? to : value)}</span>;
};
